import { useState, useEffect, useMemo } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { API_BASE_URL, authHeaders, authRequest, clearToken, defaultCreatorInfo, getToken, withCreatorDefaults } from './lib/api';
import { getExampleCreatorUsername, getSignupAttribution, track } from './lib/analytics';
import { useSaveSection } from './lib/useSaveSection';
import { navigate, useRoute } from './lib/navigation';
import ClaimPage from './pages/claim/ClaimPage';
import CollaborationDetailPage from './pages/collaborations/CollaborationDetailPage';
import CollaborationsPage from './pages/collaborations/CollaborationsPage';
import ContactPage from './pages/contact/ContactPage';
import DashboardLayout from './layouts/DashboardLayout';
import LandingPage from './pages/landing/LandingPage';
import LoginPage from './pages/login/LoginPage';
import NewCollaborationPage from './pages/collaborations/NewCollaborationPage';
import EditCollaborationPage from './pages/collaborations/EditCollaborationPage';
import NewPortfolioPage from './pages/portfolio/NewPortfolioPage';
import EditPortfolioPage from './pages/portfolio/EditPortfolioPage';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import PortfolioPage from './pages/portfolio/PortfolioPage';
import PreviewProfilePage from './pages/public-profile/PreviewProfilePage';
import ProfilePage from './pages/profile/ProfilePage';
import PublicProfileByUsernamePage from './pages/public-profile/PublicProfileByUsernamePage';
import PublicProfilePage from './pages/public-profile/PublicProfilePage';
import RequireAuth from './components/RequireAuth';
import SignupPage from './pages/signup/SignupPage';
import SocialsPage from './pages/socials/SocialsPage';
import ToolsPage from './pages/tools/ToolsPage';
import BrandOpportunitiesPage from './pages/opportunities/BrandOpportunitiesPage';

function defaultIdentity() {
  return { onboardingComplete: false, creator: defaultCreatorInfo() };
}

function App() {
  const [auth, setAuth] = useState({ status: 'loading', user: null });
  const [identity, setIdentity] = useState(null);
  const [toast, setToast] = useState(null);
  const path = useRoute();
  const saveBasics = useSaveSection('/v1/profiles/me/basics');

  useEffect(() => {
    const creatorUsername = getExampleCreatorUsername();
    track('page_view', { path, referrer: document.referrer, ...(creatorUsername ? { creatorUsername } : {}) });
  }, [path]);

  useEffect(() => {
    if (!getToken()) {
      setAuth({ status: 'anonymous', user: null });
      return;
    }
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/v1/auth/me`, {
          headers: { Accept: 'application/json', ...authHeaders() },
        });
        if (response.status === 401) {
          clearToken();
          setAuth({ status: 'anonymous', user: null });
          return;
        }
        if (!response.ok) throw new Error(`Backend returned ${response.status}`);
        const payload = await response.json();
        setAuth({ status: 'authenticated', user: payload.user });
        setIdentity(payload.profile ? { ...payload.profile, creator: withCreatorDefaults(payload.profile.creator) } : defaultIdentity());
      } catch (error) {
        setAuth({ status: 'anonymous', user: null });
      }
    })();
  }, []);

  const authActions = useMemo(
    () => ({
      signup: async (email, password) => {
        const payload = await authRequest('/v1/auth/signup', { email, password, ...getSignupAttribution() });
        setAuth({ status: 'authenticated', user: payload.user });
        setIdentity(payload.profile ? { ...payload.profile, creator: withCreatorDefaults(payload.profile.creator) } : defaultIdentity());
        track('signup_completed');
      },
      login: async (email, password) => {
        const payload = await authRequest('/v1/auth/login', { email, password });
        setAuth({ status: 'authenticated', user: payload.user });
        setIdentity(payload.profile ? { ...payload.profile, creator: withCreatorDefaults(payload.profile.creator) } : defaultIdentity());
        track('login_completed');
      },
      logout: async () => {
        await fetch(`${API_BASE_URL}/v1/auth/logout`, { method: 'POST', headers: authHeaders() }).catch(() => {});
        clearToken();
        setAuth({ status: 'anonymous', user: null });
        setIdentity(null);
        track('logout');
      },
      claim: async (claimCode) => {
        const payload = await authRequest('/v1/profiles/claim', { claim_code: claimCode });
        setIdentity({ ...payload.profile, creator: withCreatorDefaults(payload.profile.creator) });
        track('profile_claimed');
      },
    }),
    []
  );

  const identityActions = useMemo(
    () => ({
      updateCreator: (patch) => {
        track(patch.published === undefined ? 'profile_basics_updated' : patch.published ? 'profile_published' : 'profile_unpublished');
        let previousCreator;
        setIdentity((current) => {
          previousCreator = current.creator;
          const next = { ...current, creator: { ...current.creator, ...patch } };
          saveBasics(() => ({ onboardingComplete: next.onboardingComplete, creator: next.creator })).then(async (response) => {
            if (!response?.ok) {
              const payload = await response?.json().catch(() => ({}));
              const fieldMessage = payload?.error?.fields && Object.values(payload.error.fields)[0];
              throw new Error(fieldMessage || payload?.error?.message || 'Could not update profile');
            }
            setToast('Profile updated');
          }).catch((error) => {
            setIdentity((latest) => ({
              ...latest,
              creator: {
                ...latest.creator,
                ...Object.fromEntries(
                  Object.keys(patch).map((key) => [key, latest.creator[key] === patch[key] ? previousCreator[key] : latest.creator[key]])
                ),
              },
            }));
            setToast({ message: error.message || 'Could not update profile', severity: 'error' });
          });
          return next;
        });
      },
      completeOnboarding: async (creator) => {
        const previous = identity;
        const next = {
          ...identity,
          onboardingComplete: true,
          creator: { ...identity.creator, ...creator, published: false },
        };
        setIdentity(next);
        try {
          const response = await saveBasics(() => ({ onboardingComplete: true, creator: next.creator }));
          if (!response?.ok) {
            const payload = await response?.json().catch(() => ({}));
            const fieldMessage = payload?.error?.fields && Object.values(payload.error.fields)[0];
            throw new Error(fieldMessage || payload?.error?.message || 'Could not complete onboarding');
          }
          track('onboarding_completed');
          setToast('Onboarding complete');
        } catch (error) {
          setIdentity(previous);
          setToast({ message: error.message || 'Could not complete onboarding', severity: 'error' });
          throw error;
        }
      },
      showToast: (message) => setToast(message),
      showErrorToast: (message) => setToast({ message, severity: 'error' }),
      clearToast: () => setToast(null),
    }),
    [identity, saveBasics]
  );

  if (path === '/' || path.startsWith('/landing/')) return <LandingPage auth={auth} />;
  if (path.startsWith('/example')) return <PublicProfilePage auth={auth} />;
  if (path.startsWith('/profile/')) {
    const username = path.slice('/profile/'.length).split('/')[0];
    if (username) return <PublicProfileByUsernamePage auth={auth} username={username} />;
  }

  const route = renderRoute(path, { auth, identity, identityActions, authActions });
  const toastMessage = typeof toast === 'string' ? toast : toast && toast.message;
  const toastSeverity = (typeof toast === 'object' && toast && toast.severity) || 'success';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {route}
      {toast && (
        <Snackbar
          open
          autoHideDuration={2800}
          onClose={identityActions.clearToast}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={identityActions.clearToast}
            severity={toastSeverity}
            variant="filled"
            iconMapping={{ error: <CancelOutlinedIcon fontSize="inherit" /> }}
            sx={{ width: '100%' }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}

function renderRoute(path, app) {
  const { auth, identity, identityActions, authActions } = app;

  const dash = (children) => (
    <RequireAuth auth={auth} path={path}>
      <DashboardLayout identity={identity} auth={auth} authActions={authActions} identityActions={identityActions}>
        {children}
      </DashboardLayout>
    </RequireAuth>
  );

  if (path === '/contact') return <ContactPage auth={auth} />;
  if (path === '/login') return <LoginPage authActions={authActions} />;
  if (path === '/signup') return <SignupPage authActions={authActions} />;
  if (path === '/onboarding')
    return (
      <RequireAuth auth={auth} path={path}>
        <OnboardingPage identity={identity} identityActions={identityActions} />
      </RequireAuth>
    );
  if (path === '/claim')
    return (
      <RequireAuth auth={auth} path={path}>
        <ClaimPage authActions={authActions} />
      </RequireAuth>
    );
  if (path === '/dashboard/preview')
    return (
      <RequireAuth auth={auth} path={path}>
        <PreviewProfilePage auth={auth} />
      </RequireAuth>
    );
  if (path === '/dashboard') return dash(<ProfilePage identity={identity} identityActions={identityActions} auth={auth} />);
  if (path === '/dashboard/profile') return dash(<ProfilePage identity={identity} identityActions={identityActions} auth={auth} />);
  if (path === '/dashboard/socials') return dash(<SocialsPage identityActions={identityActions} />);
  if (path === '/dashboard/portfolio') return dash(<PortfolioPage identityActions={identityActions} />);
  if (path === '/dashboard/portfolio/new') return dash(<NewPortfolioPage identityActions={identityActions} />);
  if (path.startsWith('/dashboard/portfolio/edit/')) {
    const id = path.split('/').pop();
    return dash(<EditPortfolioPage id={id} identityActions={identityActions} />);
  }
  if (path === '/dashboard/collaborations') return dash(<CollaborationsPage identityActions={identityActions} />);
  if (path === '/dashboard/tools') return dash(<ToolsPage />);
  if (path === '/dashboard/opportunities') return dash(<BrandOpportunitiesPage creator={identity.creator} />);
  if (path === '/dashboard/collaborations/new') return dash(<NewCollaborationPage identityActions={identityActions} />);
  if (path.startsWith('/dashboard/collaborations/edit/')) {
    const id = path.split('/').pop();
    return dash(<EditCollaborationPage id={id} identityActions={identityActions} />);
  }
  if (path.startsWith('/dashboard/collaborations/')) {
    const id = path.split('/').pop();
    return dash(<CollaborationDetailPage id={id} identityActions={identityActions} />);
  }
  if (path === '/dashboard/settings') return dash(<ProfilePage identity={identity} identityActions={identityActions} auth={auth} />);
  navigate('/');
  return null;
}

export default App;
